import {
  CircularProgress,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { Fragment, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { StudiesPrice } from '../../../../shared/hooks/hospitalHook';
import { byteToString } from '../../../../shared/utils/dataFormat';
import {
  MobileTable,
  RowContainer,
  RowLabel,
  RowValue,
  TextAlign,
} from '../../common/components/MobileTable';
import {
  HscanTable,
  HscanTableCell,
} from '../../common/components/SortableTable/Table';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';
import { krwCurrencyFormatter as formatter } from '../../utils';

import { VolumePrice2ModelInfo } from './VolumePrice2ModelInfo';
import { VolumePriceModelInfo } from './VolumePriceModelInfo';

const OnlyOnlineText = styled.span`
  color: #ff5656;
`;
export const TaxField = styled.div`
  text-align: center;
  font-size: 12px;
  color: #a3a6ab;
  margin-top: 4px;
`;

interface PaymentTableProps {
  useMailPerHospital: Record<string, boolean>;
  pricePerHospital: Record<string, StudiesPrice>;
  total: number;
  withCd: boolean;
  deliveryFee: number;
}
export const PaymentTable = (props: PaymentTableProps) => {
  const windowSize = useContext(WindowSizeContext);
  const isMobile = windowSize === WindowSize.MOBILE;

  return isMobile ? (
    <MobilePaymentTable {...props} />
  ) : (
    <NormalPaymentTable {...props} />
  );
};

const NormalPaymentTable = (props: PaymentTableProps) => {
  const { useMailPerHospital, pricePerHospital, total, withCd, deliveryFee } =
    props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.payment.table',
  });

  const pricePerHospitalEntries = useMemo(
    () => Object.entries(pricePerHospital),
    [pricePerHospital],
  );

  return (
    <HscanTable
      head={
        <TableHead>
          <TableRow>
            <HscanTableCell align="center">{t('column.item')}</HscanTableCell>
            <HscanTableCell align="center">{t('column.price')}</HscanTableCell>
          </TableRow>
        </TableHead>
      }
      body={
        <TableBody>
          {pricePerHospitalEntries.length ? (
            <>
              {pricePerHospitalEntries.map(([hospitalId, price]) => (
                <Fragment key={hospitalId}>
                  <TableRow>
                    <HscanTableCell align="center" $border="1px solid">
                      {price.hospitalName}&nbsp;/&nbsp;
                      {t('column.case', { case: price.numStudies })}
                      &nbsp;/&nbsp;
                      {t('column.onlinePrice')}
                      &nbsp;/&nbsp;
                      {useMailPerHospital[hospitalId] ? (
                        t('column.mailingIncluded')
                      ) : (
                        <Trans
                          t={t}
                          i18nKey="column.onlyOnline"
                          components={[<OnlyOnlineText />]}
                        />
                      )}
                    </HscanTableCell>
                    <HscanTableCell align="center" $border="1px solid">
                      {price.isPriceLoading ? (
                        <CircularProgress size={14} />
                      ) : price.isPriceError ? (
                        <>{t('error.priceError')}</>
                      ) : (
                        <>
                          {t('column.priceField', {
                            price: formatter(price.price),
                          })}{' '}
                          {price.size && `(~ ${byteToString(price.size)})`}
                        </>
                      )}
                      {price.priceModel?.type === 'VOLUME' ? (
                        <VolumePriceModelInfo
                          volumePriceModel={price.priceModel}
                        />
                      ) : null}
                      {price.priceModel?.type === 'VOLUME2' ? (
                        <VolumePrice2ModelInfo
                          volumePrice2Model={price.priceModel}
                        />
                      ) : null}
                      {
                        <TaxField>
                          {t('column.hospitalPrice', {
                            price: formatter(price.taxFree),
                          })}
                          {', '}
                          {t('column.servicePrice', {
                            price: formatter(
                              Math.round(
                                (price.price - price.taxFree) * (10 / 11),
                              ),
                            ),
                          })}
                          {', '}
                          {t('column.vatPrice', {
                            price: formatter(
                              Math.round(
                                (price.price - price.taxFree) * (1 / 11),
                              ),
                            ),
                          })}
                        </TaxField>
                      }
                    </HscanTableCell>
                  </TableRow>
                </Fragment>
              ))}
              {withCd && (
                <TableRow>
                  <HscanTableCell align="center" $border="1px solid">
                    {t('column.mailingPrice')}
                  </HscanTableCell>
                  <HscanTableCell align="center" $border="1px solid">
                    {t('column.priceField', { price: formatter(deliveryFee) })}
                  </HscanTableCell>
                </TableRow>
              )}
            </>
          ) : (
            <TableRow>
              <HscanTableCell align="center" colSpan={2}>
                {t('message.empty')}
              </HscanTableCell>
            </TableRow>
          )}
        </TableBody>
      }
      footer={
        <TableFooter>
          <TableRow>
            <HscanTableCell align="center">
              {t('column.totalPrice')}
            </HscanTableCell>
            <HscanTableCell align="center">
              {t('column.priceField', {
                price: formatter(total),
              })}
            </HscanTableCell>
          </TableRow>
        </TableFooter>
      }
    />
  );
};

interface MobileTableRowProps {
  label: React.ReactNode;
  align?: TextAlign;
  children: React.ReactNode;
  onClick?: () => void;
}
const PaymentRowLabel = styled(RowLabel)`
  width: 116px;
  padding: 16px 8px;
`;
const PaymentRowValue = styled(RowValue)`
  width: 174px;
  padding: 16px 8px;
`;
export const PaymentMobileTableRow = (props: MobileTableRowProps) => {
  const { label, children, align, onClick } = props;
  return (
    <RowContainer onClick={onClick}>
      <PaymentRowLabel>{label}</PaymentRowLabel>
      <PaymentRowValue align={align}>{children}</PaymentRowValue>
    </RowContainer>
  );
};
const MobilePaymentTable = (props: PaymentTableProps) => {
  const { useMailPerHospital, pricePerHospital, total, withCd, deliveryFee } =
    props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.payment.table',
  });

  const pricePerHospitalEntries = useMemo(
    () => Object.entries(pricePerHospital),
    [pricePerHospital],
  );

  return (
    <MobileTable>
      <tbody>
        {pricePerHospitalEntries.length ? (
          pricePerHospitalEntries.map(([hospitalId, price]) => (
            <React.Fragment key={hospitalId}>
              <PaymentMobileTableRow label={t('column.item')} align="right">
                {price.hospitalName}/<br />
                <span>
                  {t('column.case', { case: price.numStudies })}/&nbsp;
                  {useMailPerHospital[hospitalId] ? (
                    t('column.mailingIncluded')
                  ) : (
                    <Trans
                      t={t}
                      i18nKey="column.mobileOnlyOnline"
                      components={[<OnlyOnlineText />]}
                    />
                  )}
                </span>
              </PaymentMobileTableRow>
              <PaymentMobileTableRow
                label={t('column.onlinePrice')}
                align="right">
                {price.isPriceLoading ? (
                  <CircularProgress size={14} />
                ) : price.isPriceError ? (
                  <>{t('error.priceError')}</>
                ) : (
                  <>
                    {t('column.priceField', {
                      price: formatter(price.price),
                    })}{' '}
                    {price.size && `(~ ${byteToString(price.size)})`}
                  </>
                )}
                {price.priceModel?.type === 'VOLUME' ? (
                  <VolumePriceModelInfo volumePriceModel={price.priceModel} />
                ) : null}
                {price.priceModel?.type === 'VOLUME2' ? (
                  <VolumePrice2ModelInfo volumePrice2Model={price.priceModel} />
                ) : null}
                {
                  <TaxField>
                    {t('column.hospitalPrice', {
                      price: formatter(price.taxFree),
                    })}
                    <br />
                    {t('column.servicePrice', {
                      price: formatter(
                        Math.round((price.price - price.taxFree) * (10 / 11)),
                      ),
                    })}
                    <br />
                    {t('column.vatPrice', {
                      price: formatter(
                        Math.round((price.price - price.taxFree) * (1 / 11)),
                      ),
                    })}
                  </TaxField>
                }
              </PaymentMobileTableRow>
            </React.Fragment>
          ))
        ) : (
          <RowContainer>
            <RowValue colSpan={2}>{t('message.empty')}</RowValue>
          </RowContainer>
        )}
        {withCd && (
          <PaymentMobileTableRow label={t('column.mailingPrice')} align="right">
            {t('column.priceField', { price: formatter(deliveryFee) })}
          </PaymentMobileTableRow>
        )}
      </tbody>
      <tfoot>
        <PaymentMobileTableRow
          label={
            <Trans
              t={t}
              i18nKey="column.mobileTotalPrice"
              components={[
                <br />,
                <span style={{ fontSize: '10px', fontWeight: 300 }} />,
              ]}
            />
          }
          align="right">
          {t('column.priceField', {
            price: formatter(total),
          })}
        </PaymentMobileTableRow>
      </tfoot>
    </MobileTable>
  );
};
